import React from "react";
import "./styles.css";

const CAPM = () => {
  return (
    <div className="container">
      <h1>Understanding the Capital Asset Pricing Model (CAPM) and Beta</h1>
      <h2>What is the Capital Asset Pricing Model (CAPM)?</h2>
      <p>The Capital Asset Pricing Model (CAPM) is a model used to determine the theoretically appropriate required rate of return of an asset, given its risk as measured by beta. It helps investors assess whether an investment is adequately compensating them for the risk they are taking, relative to a risk-free asset and the broader market.</p>
      <p><strong>Formula:</strong></p>
      <img src="assets/capm.png" alt="Capital Asset Pricing Model" />
      <p>Where:</p>
      <ul>
        <li><strong>R<sub>f</sub></strong> = Risk-free rate (e.g., yield on government bonds)</li>
        <li><strong>β</strong> = Beta of the investment (measures systematic risk)</li>
        <li><strong>R<sub>m</sub></strong> = Expected market return</li>
        <li><strong>(R<sub>m</sub> - R<sub>f</sub>)</strong> = Market risk premium (the excess return expected from the market over the risk-free rate)</li>
      </ul>
      <h2>What is Beta?</h2>
      <p>Beta (β) is a measure of an asset's systematic risk, which is the risk inherent to the entire market or market segment. It quantifies how much an asset's price is expected to move relative to changes in the market. Here’s how to interpret beta values:</p>
      <ul>
        <li><strong>β=1:</strong> The asset moves in line with the market.</li>
        <li><strong>β{">"}1:</strong> The asset is more volatile than the market.</li>
        <li><strong>β{"1"}:</strong> The asset is less volatile than the market.</li>
        <li><strong>β=0:</strong> The asset’s returns are uncorrelated with the market (e.g., risk-free assets like Treasury bills).</li>
        <li><strong>β{"<"}0:</strong> The asset moves inversely to the market (rare, but possible for certain hedging instruments).</li>
      </ul>
      <h2>How is Beta Determined?</h2>
      <p>Beta is calculated using regression analysis, which measures the relationship between the returns of an asset and the returns of the market.</p>
      <ul>
        <li><strong>Step 1:</strong> Collect historical price data for the asset and the market index.</li>
        <li><strong>Step 2:</strong> Calculate periodic returns for both the asset and the market.</li>
        <li><strong>Step 3:</strong> Run a regression analysis to find the line of best fit. The slope of this line is the beta.</li>
        <li><strong>Step 4:</strong> Interpret the results to understand the asset’s risk relative to the market.</li>
      </ul>
      <h2>Limitations of Beta</h2>
      <p>While beta is a useful measure of systematic risk, it has some limitations:</p>
      <ul>
        <li><strong>Historical Data:</strong> Beta is based on past data, which may not accurately predict future risk.</li>
        <li><strong>Market Dependency:</strong> Beta assumes that the market is the only source of risk, ignoring other factors like company-specific risks.</li>
        <li><strong>Volatility vs. Risk:</strong> Beta measures volatility, which is not always synonymous with risk.</li>
      </ul>
      <h2>Why is Beta Important in CAPM?</h2>
      <p>Beta is a key input in the CAPM formula because it quantifies the asset’s exposure to market risk. By incorporating beta, CAPM provides a way to estimate the expected return of an asset based on its risk relative to the market.</p>
    </div>
  );
};

export default CAPM;